import { useEffect, useState } from "react";
import { Power, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * BookingsKillSwitch — Admin toggle that closes/opens the public booking
 * form on `/cliente/agendar`. When OFF:
 *   • Clients see a banner ("Reservas em pausa") with WhatsApp + e-mail
 *   • Backend rejects POST /bookings from client accounts (503)
 *   • Admins can still create bookings normally
 *
 * Admins can edit the displayed message for clients (defaults to a
 * sensible fallback when blank).
 */
export default function BookingsKillSwitch() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingMsg, setEditingMsg] = useState(false);
  const [draftMsg, setDraftMsg] = useState("");

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => {
        setSettings(data);
        setDraftMsg(data.bookings_closed_message || "");
      })
      .catch(() => {});
  }, []);

  async function toggle() {
    if (!settings) return;
    const next = !settings.bookings_enabled;
    if (!next && !window.confirm(
      "Vai fechar as reservas online. Os clientes vão ver um banner pedindo para contactar diretamente. Continuar?",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/settings/bookings-enabled", {
        enabled: next,
        closed_message: draftMsg || null,
      });
      setSettings(data);
      toast.success(next ? "Reservas online ATIVADAS" : "Reservas online FECHADAS");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  }

  async function saveMessage() {
    if (!settings) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/settings/bookings-enabled", {
        enabled: settings.bookings_enabled,
        closed_message: draftMsg || null,
      });
      setSettings(data);
      setEditingMsg(false);
      toast.success("Mensagem atualizada");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  }

  if (!settings) return null;
  const on = settings.bookings_enabled;
  const tone = on
    ? "border-[#1a4640] bg-[#062c27]"
    : "border-[#dc2626] bg-[#dc2626]/10";
  return (
    <section className={`lux-card p-5 border-2 ${tone}`} data-testid="bookings-kill-switch">
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`w-12 h-12 rounded-full grid place-items-center ${on ? "bg-[#10b981]/15 text-[#10b981]" : "bg-[#dc2626] text-white"}`}>
          {on ? <Unlock size={22} /> : <Lock size={22} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0b2af]">
            Página de reservas online
          </div>
          <div className={`font-display text-2xl mt-1 ${on ? "text-[#10b981]" : "text-[#fca5a5]"}`}
               data-testid="bookings-kill-switch-status">
            {on ? "ATIVA · clientes podem reservar" : "FECHADA · clientes veem mensagem de contacto"}
          </div>
          <div className="text-xs text-[#a0b2af] mt-1">
            {on
              ? "O formulário /cliente/agendar está aberto a todos os clientes."
              : "POST /bookings está bloqueado para clientes. Admins continuam a poder criar reservas."}
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
                className={`px-5 py-2.5 text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${
                  on
                    ? "border border-[#fca5a5] text-[#fca5a5] hover:bg-[#dc2626]/15"
                    : "bg-[#10b981] text-[#010c0a] hover:bg-[#34d399]"
                }`}
                data-testid="bookings-kill-switch-btn">
          <Power size={14} />
          {on ? "Desligar reservas" : "Ligar reservas"}
        </button>
      </div>

      {/* Mensagem mostrada ao cliente quando reservas estão fechadas */}
      {!on && (
        <div className="mt-4 pt-4 border-t border-[#dc2626]/30">
          <div className="text-[10px] uppercase tracking-widest text-[#fca5a5] mb-2">
            Mensagem mostrada aos clientes
          </div>
          {editingMsg ? (
            <div className="space-y-2">
              <textarea value={draftMsg} onChange={(e) => setDraftMsg(e.target.value)}
                        rows={3}
                        className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                        data-testid="bookings-closed-msg-input"
                        placeholder="Deixe vazio para usar a mensagem padrão" />
              <div className="flex gap-2">
                <button onClick={saveMessage} disabled={busy}
                        className="btn-gold px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
                        data-testid="bookings-closed-msg-save">
                  Guardar mensagem
                </button>
                <button onClick={() => { setEditingMsg(false); setDraftMsg(settings.bookings_closed_message || ""); }}
                        className="px-4 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:text-[#fdfbf7]">
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <p className="flex-1 text-sm text-[#fdfbf7] leading-relaxed bg-[#062c27] border border-[#1a4640] p-3">
                {settings.bookings_closed_message
                  || "As reservas online estão temporariamente fechadas. Por favor contacte-nos pelo WhatsApp +351 967 386 076 ou por e-mail luxstaymadeira@gmail.com."}
              </p>
              <button onClick={() => setEditingMsg(true)}
                      className="px-3 py-2 text-[10px] uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10"
                      data-testid="bookings-closed-msg-edit">
                Editar
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
